import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CalculationStatus from '../../components/ui/CalculationStatus';
import Button from '../../components/ui/Button';
import DateTimeSection from './components/DateTimeSection';
import TimezoneSection from './components/TimezoneSection';
import LocationSection from './components/LocationSection';
import HouseSystemSection from './components/HouseSystemSection';
import Icon from '../../components/AppIcon';
import { getBirthChartData } from '../../services/astrologyApi';

const BirthDataInput = () => {
  const navigate = useNavigate();
  const [calculationStatus, setCalculationStatus] = useState('idle');
  const [calculationError, setCalculationError] = useState('');

  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: { hour: '', minute: '' },
    unknownTime: false,
    timezone: 'UTC+7',
    city: '',
    country: '',
    showAdvanced: false,
    latitude: '',
    longitude: '',
    houseSystem: 'placidus'
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.birthDate) {
      newErrors.birthDate = 'กรุณาเลือกวันที่เกิด (Please select birth date)';
    }

    if (!formData?.unknownTime) {
      if (!formData?.birthTime?.hour || !formData?.birthTime?.minute) {
        newErrors.birthTime = 'กรุณาเลือกเวลาเกิด หรือเลือก "ไม่ทราบเวลาเกิด" (Please select birth time or check "Unknown birth time")';
      }
    }

    if (!formData?.timezone) {
      newErrors.timezone = 'กรุณาเลือกเขตเวลา (Please select timezone)';
    }

    if (!formData?.city) {
      newErrors.city = 'กรุณากรอกชื่อเมือง (Please enter city name)';
    }

    if (!formData?.country) {
      newErrors.country = 'กรุณากรอกชื่อประเทศ (Please enter country name)';
    }

    if (!formData?.houseSystem) {
      newErrors.houseSystem = 'กรุณาเลือกระบบบ้าน (Please select house system)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleCalculate = async () => {
    if (!validateForm()) {
      setCalculationStatus('error');
      setCalculationError('กรุณากรอกข้อมูลให้ครบถ้วน (Please complete all required fields)');
      setTimeout(() => {
        setCalculationStatus('idle');
        setCalculationError('');
      }, 4000);
      return;
    }

    setCalculationStatus('loading');
    setCalculationError('');

    try {
      const result = await getBirthChartData(formData);

      if (result?.success) {
        setCalculationStatus('success');
        
        setTimeout(() => {
          navigate('/astrological-results', { 
            state: { 
              birthData: formData,
              calculatedAt: new Date()?.toISOString(),
              astrologicalData: result?.data
            } 
          });
        }, 1500);
      } else {
        setCalculationStatus('error');
        setCalculationError(
          result?.error || 'เกิดข้อผิดพลาดในการคำนวณ กรุณาลองใหม่อีกครั้ง (Calculation error occurred. Please try again.)'
        );
        
        setTimeout(() => {
          setCalculationStatus('idle');
          setCalculationError('');
        }, 4000);
      }
    } catch (error) {
      setCalculationStatus('error');
      setCalculationError('เกิดข้อผิดพลาดในการเชื่อมต่อ API กรุณาตรวจสอบการตั้งค่า (API connection error. Please check your settings.)');
      
      setTimeout(() => {
        setCalculationStatus('idle');
        setCalculationError('');
      }, 4000);
    }
  };

  const handleRetry = () => {
    setCalculationStatus('idle');
    setCalculationError('');
  };

  return (
    <div className="min-h-screen bg-gradient-cosmic relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-2 h-2 bg-cosmic-gold rounded-full animate-twinkle" />
        <div className="absolute top-40 right-20 w-1 h-1 bg-cosmic-lightGold rounded-full animate-twinkle" style={{ animationDelay: '1s' }} />
        <div className="absolute top-60 left-1/4 w-1.5 h-1.5 bg-cosmic-gold rounded-full animate-twinkle" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-40 right-1/3 w-2 h-2 bg-cosmic-lightGold rounded-full animate-twinkle" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-20 left-1/2 w-1 h-1 bg-cosmic-gold rounded-full animate-twinkle" style={{ animationDelay: '1.5s' }} />
      </div>
      
      <Header />
      <CalculationStatus 
        status={calculationStatus} 
        error={calculationError}
        onRetry={handleRetry}
      />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="mb-16 text-center">
            <div className="flex items-center justify-center mb-8">
              <div className="relative">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-gold rounded-full flex items-center justify-center shadow-gold">
                  <Icon name="Star" size={40} color="#0a1128" className="md:w-12 md:h-12" />
                </div>
                <div className="absolute inset-0 bg-cosmic-gold rounded-full opacity-20 blur-2xl animate-pulse-subtle" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-cosmic-gold mb-4">
              เว็บคำนวณดวงชะตาเกิดแบบมืออาชีพ
            </h1>
            
            <p className="text-lg md:text-xl text-cosmic-lightGold max-w-3xl mx-auto leading-relaxed font-thai mb-8">
              วิเคราะห์ตำแหน่งดาวและโครงสร้างดวงอย่างละเอียด<br />
              ด้วยระบบโหราศาสตร์มาตรฐานสากล
            </p>
            
            <Button
              variant="default"
              size="xl"
              iconName="ArrowDown"
              iconPosition="right"
              onClick={() => document.getElementById('form-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-gold text-cosmic-navy font-semibold shadow-gold hover:shadow-glow-lg"
            >
              คำนวณดวงชะตาของคุณ
            </Button>
          </div>

          {/* Birth Chart Form Area */}
          <div id="form-section" className="relative mb-16">
            <div className="absolute inset-0 bg-cosmic-gold/5 rounded-3xl blur-3xl" />
            <div className="relative bg-cosmic-midnight/80 backdrop-blur-xl border-2 border-cosmic-gold/30 rounded-3xl shadow-cosmic p-8 md:p-12">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-cosmic-gold mb-3">
                  กรอกข้อมูลการเกิด
                </h2>
                <p className="text-cosmic-lightGold/80 font-thai">
                  ข้อมูลที่แม่นยำจะให้ผลการวิเคราะห์ที่ละเอียดมากขึ้น
                </p>
              </div>

              <div className="space-y-8">
                <DateTimeSection
                  birthDate={formData?.birthDate}
                  birthTime={formData?.birthTime}
                  unknownTime={formData?.unknownTime}
                  onDateChange={(value) => setFormData({ ...formData, birthDate: value })}
                  onTimeChange={(value) => setFormData({ ...formData, birthTime: value })}
                  onUnknownTimeChange={(value) => setFormData({ ...formData, unknownTime: value })}
                  errors={errors}
                />

                <div className="border-t border-cosmic-gold/20" />

                <TimezoneSection
                  timezone={formData?.timezone}
                  onTimezoneChange={(value) => setFormData({ ...formData, timezone: value })}
                  error={errors?.timezone}
                />

                <div className="border-t border-cosmic-gold/20" />

                <LocationSection
                  city={formData?.city}
                  country={formData?.country}
                  showAdvanced={formData?.showAdvanced}
                  latitude={formData?.latitude}
                  longitude={formData?.longitude}
                  onCityChange={(value) => setFormData({ ...formData, city: value })}
                  onCountryChange={(value) => setFormData({ ...formData, country: value })}
                  onShowAdvancedChange={(value) => setFormData({ ...formData, showAdvanced: value })}
                  onLatitudeChange={(value) => setFormData({ ...formData, latitude: value })}
                  onLongitudeChange={(value) => setFormData({ ...formData, longitude: value })}
                  errors={errors}
                />

                <div className="border-t border-cosmic-gold/20" />

                <HouseSystemSection
                  houseSystem={formData?.houseSystem}
                  onHouseSystemChange={(value) => setFormData({ ...formData, houseSystem: value })}
                  error={errors?.houseSystem}
                />

                <div className="border-t border-cosmic-gold/20 pt-8">
                  <Button
                    variant="default"
                    size="xl"
                    fullWidth
                    iconName="Calculator"
                    iconPosition="left"
                    onClick={handleCalculate}
                    loading={calculationStatus === 'loading'}
                    disabled={calculationStatus === 'loading'}
                    className="bg-gradient-gold text-cosmic-navy text-xl py-6 font-bold shadow-gold hover:shadow-glow-lg"
                  >
                    เริ่มคำนวณ Birth Chart
                  </Button>

                  <p className="text-sm text-cosmic-lightGold/60 text-center mt-4 font-thai">
                    การคำนวณจะใช้เวลาประมาณ 3-5 วินาที
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust & Credibility Section */}
          <div className="bg-cosmic-midnight/60 backdrop-blur-sm border border-cosmic-gold/20 rounded-2xl p-8 md:p-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-cosmic-gold mb-3">
                ความแม่นยำและมาตรฐานระดับมืออาชีพ
              </h2>
              <p className="text-cosmic-lightGold/80 font-thai">
                ระบบคำนวณที่คุณไว้วางใจได้
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-cosmic-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="Award" size={32} className="text-cosmic-gold" />
                </div>
                <h3 className="text-lg font-semibold text-cosmic-gold mb-2 font-thai">
                  คำนวณตามระบบโหราศาสตร์สากล
                </h3>
                <p className="text-sm text-cosmic-lightGold/70 font-thai">
                  ใช้มาตรฐานการคำนวณที่ได้รับการยอมรับทั่วโลก
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-cosmic-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={32} className="text-cosmic-gold" />
                </div>
                <h3 className="text-lg font-semibold text-cosmic-gold mb-2 font-thai">
                  เหมาะสำหรับนักโหรและผู้สนใจจริง
                </h3>
                <p className="text-sm text-cosmic-lightGold/70 font-thai">
                  ข้อมูลครบถ้วนสำหรับการศึกษาและวิเคราะห์เชิงลึก
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-cosmic-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-cosmic-gold" />
                </div>
                <h3 className="text-lg font-semibold text-cosmic-gold mb-2 font-thai">
                  ใช้งานง่าย อ่านผลลัพธ์ชัดเจน
                </h3>
                <p className="text-sm text-cosmic-lightGold/70 font-thai">
                  ออกแบบให้เข้าใจง่าย พร้อมคำอธิบายภาษาไทย
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-cosmic-gold/20 bg-cosmic-midnight/80 backdrop-blur-xl py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-cosmic-lightGold/60 text-sm font-thai">
              © 2026 ThaiAstroChart - ระบบคำนวณดวงชะตาเกิดแบบมืออาชีพ
            </p>
            <p className="text-cosmic-lightGold/40 text-xs mt-2">
              Professional Birth Chart Calculation System
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BirthDataInput;